import { giphyBlack, giphyDarkCharcoal, giphyLightGrey, giphyWhite } from '@giphy/js-brand'
import { IGif } from '@giphy/js-types'
import { Gif as Gif_ } from '@giphy/react-components'
import React, { useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLocation } from 'react-use'
import { webFetch } from 'shared/api'
import ModalContext from 'shared/components/context/modal'
import UserContext from 'shared/components/context/user'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import routes from 'shared/routes'
import styled from 'styled-components'
import Avatar from './avatar'

// need this so long names truncate
const containerWidth = 176

const Menu = styled.ul`
    display: none;
    top: 36px;
    position: absolute;
    right: 0;
    z-index: 100;
    margin: 0;
    width: 100%;
    background-color: ${giphyDarkCharcoal};

    li {
        display: flex;
        border-top: 1px solid #515151;
        list-style: none;
        margin: 0;
        text-align: center;
        &:first-of-type {
            border-top: 4px solid ${giphyBlack};
        }

        a {
            width: 100%;
            background-color: transparent;
            display: block;
            float: none;
            margin: 0;
            padding: 10px 15px;
            cursor: pointer;
            transition: background-color 0.1s ease-in-out;
            font-size: 15px;
            &:hover {
                background: ${giphyLightGrey};
            }
        }
    }
`

const Container = styled.div`
    margin: 10px 0 0;
    float: right;
    color: ${giphyWhite};
    font-weight: bold;
    position: relative;
    width: ${containerWidth}px;
    &:hover > ul {
        display: block;
    }
`

const Username = styled.div`
    height: 36px;
    display: inline-block;
    line-height: 36px;
    min-width: 100px;
    text-transform: none;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    font-size: 15px;
    user-select: none;
    flex: 1;
    padding-left: 10px;
`

const UserContainer = styled.div`
    display: flex;
    background: ${giphyDarkCharcoal};
`

const Caret = styled.div`
    color: ${giphyWhite};
    font-size: 12px;
    height: 36px;
    line-height: 44px;
    overflow: hidden;
    padding: 0;
    text-align: center;
    width: 40px;
`

const LoggedInMenu = () => {
    const { openCollectionCabinet } = useContext(ModalContext)
    const { user } = useContext(UserContext)
    if (!user.permissions) {
        return null
    }
    const {
        isStaff,
        permissions: { dashboard_link, create_stories, manage_own_subchannels, access_dvr },
    } = user
    return (
        <Menu>
            {dashboard_link && (
                <li>
                    <a href={routes.DASHBOARD.path}>Dashboard</a>
                </li>
            )}
            {manage_own_subchannels && (
                <li>
                    <a
                        href=""
                        onClick={e => {
                            e.preventDefault()
                            openCollectionCabinet()
                        }}
                    >
                        Collections
                    </a>
                </li>
            )}
            <li>
                <a href={routes.SETTINGS.path}>Settings</a>
            </li>
            {create_stories && (
                <li>
                    <a href={routes.STORYBOOK.path}>Storybook</a>
                </li>
            )}
            <li>
                <Link to={routes.FAVORITES.path}>Favorites</Link>
            </li>
            {isStaff && (
                <li>
                    <a href="/edit/channels">Channels</a>
                </li>
            )}
            {(access_dvr || isStaff) && (
                <li>
                    <a href="/supercut">Super Cut</a>
                </li>
            )}
            <li>
                <a href="/logout">Log Out</a>
            </li>
        </Menu>
    )
}

const LoggedOutMenu = () => {
    return (
        <Menu>
            <li>
                <a href={routes.LOGIN.path}>Login</a>
            </li>
            <li>
                <a href={routes.JOIN.path}>Join</a>
            </li>
            <li>
                <Link to={routes.FAVORITES.path}>Favorites</Link>
            </li>
        </Menu>
    )
}

const Gif = styled(Gif_)`
    img {
        object-fit: cover;
    }
`
const GifAvatar = () => {
    const [gif, setGif] = useState<IGif | undefined>()
    useAsyncEffect(async () => {
        // TODO randomize and have cooler dummy avatar GIFs than just the eyes
        const { data: gif } = await webFetch.gif('sDWOPiXURPdxiLlwpA')
        setGif(gif)
    }, [])
    return gif ? <Gif width={36} height={36} gif={gif} /> : null
}

const LoginOnly = styled(UserContainer)`
    ${Username} {
        margin-right: 10px;
        padding-left: 0;
    }
`
const UserIcon = styled.div`
    display: flex;
    height: 36px;
    width: 36px;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    background-color: rgba(255, 255, 255, 0.1);
    .ss-user {
        height: 14px;
    }
`
const Login = () => {
    let { user, localFavorites } = useContext(UserContext)
    const { displayName, username } = user
    const { pathname } = useLocation()
    const isLogin = pathname === routes.LOGIN.path
    const isJoin = pathname === `/join`
    if (isLogin || isJoin) {
        return null
    }
    const justLogin = !user.loggedIn && localFavorites.length === 0
    return (
        <Container>
            {justLogin ? (
                <LoginOnly as="a" href="/login">
                    <UserIcon>
                        <div className="ss-user" />
                    </UserIcon>
                    <Username>Log in</Username>
                </LoginOnly>
            ) : (
                <>
                    <UserContainer
                        onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (user.loggedIn) {
                                document.location.href = `/channel/${username}`
                            }
                        }}
                    >
                        {user.loggedIn ? <Avatar user={user} /> : <GifAvatar />}
                        <Username>{displayName || username || 'GIPHY User'}</Username>
                        <Caret className="ss-dropdown" />
                    </UserContainer>
                    {user.loggedIn ? <LoggedInMenu /> : <LoggedOutMenu />}
                </>
            )}
        </Container>
    )
}

export default Login
